import type { GroupShape } from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import type { GeoJSONLayerConfig, StaticLayerConfig } from "@/types/configs";
import {
  featureBoundingBox,
  featureCentroid,
  pointInBoundingBox,
  pointInGeoJSONFeature,
} from "@/utils/data/pointInPolygon";
import type { Layer } from "@deck.gl/core";
import { COORDINATE_SYSTEM } from "@deck.gl/core";
import {
  GeoJsonLayer,
  IconLayer,
  PathLayer,
  ScatterplotLayer,
  TextLayer,
} from "@deck.gl/layers";

// Re-export for backward compatibility
export type { GeoJSONLayerConfig, StaticLayerConfig };

export type StaticLayerKey = "municipalities" | "countries" | "timezones";

// Keep for backward compatibility if needed
export const layers: Record<StaticLayerKey, StaticLayerConfig> = {
  municipalities: {
    source: {
      type: "geojson",
      data: "/data/map/layers/municipalities.geojson",
    },
    layers: [
      {
        id: "municipalities-fill",
        type: "fill",
        paint: {
          "fill-color": "#003399",
          "fill-opacity": 0.15,
        },
      },
      {
        id: "municipalities-border",
        type: "line",
        paint: {
          "line-color": "#003399",
          "line-width": 1.8,
        },
      },
    ],
  },
  countries: {
    source: {
      type: "geojson",
      data: "/data/map/layers/countries.geojson",
    },
    layers: [
      {
        id: "countries-fill",
        type: "fill",
        paint: {
          "fill-color": "#003399",
          "fill-opacity": 0.1,
        },
      },
      {
        id: "countries-border",
        type: "line",
        paint: {
          "line-color": "#003399",
          "line-width": 1.8,
        },
      },
    ],
  },
  timezones: {
    source: {
      type: "geojson",
      data: "/data/map/layers/timezones.geojson",
    },
    layers: [
      {
        id: "timezones-fill",
        type: "fill",
        paint: {
          "fill-color": "#003399",
          "fill-opacity": 0.1,
        },
      },
      {
        id: "timezones-border",
        type: "line",
        paint: {
          "line-color": "#003399",
          "line-width": 1.8,
        },
      },
    ],
  },
};

/**
 * Create deck.gl layers for static layer data
 */
export function createStaticLayer(
  layerId: string,
  data: any,
  isVisible: boolean,
  config: StaticLayerConfig
): Layer[] {
  if (!data || !isVisible) {
    return [];
  }

  const layers: Layer[] = [];

  // Extract line/border properties from config (for outlines only)
  const lineLayer = config.layers.find((l) => l.type === "line");
  const fillLayer = config.layers.find((l) => l.type === "fill");

  const lineColorHex = lineLayer?.paint?.["line-color"] || fillLayer?.paint?.["fill-color"] || "#003399";
  const lineWidth = lineLayer?.paint?.["line-width"] || 1.8;

  // Convert hex colors to RGBA arrays
  const hexToRgba = (
    hex: string,
    opacity: number = 255
  ): [number, number, number, number] => {
    if (typeof hex === "string" && hex.startsWith("#")) {
      const hexClean = hex.replace("#", "");
      const r = parseInt(hexClean.substring(0, 2), 16);
      const g = parseInt(hexClean.substring(2, 4), 16);
      const b = parseInt(hexClean.substring(4, 6), 16);
      const a = Math.round(opacity * 255); // Convert 0-1 opacity to 0-255
      return [r, g, b, a];
    }
    return [0, 51, 153, Math.round(opacity * 255)]; // Default #003399
  };

  const lineColorRgba = hexToRgba(lineColorHex, 1.0); // Full opacity for outlines

  // Create outline-only layer for static layers (no fill, only outlines)
  layers.push(
    new GeoJsonLayer({
      id: `${layerId}-outline`,
      data,
      pickable: false,
      stroked: true,  // Enable outlines
      filled: false,  // Disable fills - static layers should only show outlines
      visible: isVisible,
      fp64: true, // Enable high precision for accurate polygon alignment with map tiles
      getLineColor: () => lineColorRgba,
      getLineWidth: () => lineWidth,
      lineWidthMinPixels: 1,
      updateTriggers: {
        getLineColor: [lineColorHex],
        getLineWidth: [lineWidth],
        data: [layerId, isVisible],
      },
    })
  );

  return layers;
}

/**
 * Calculate optimal radius based on zoom and feature count
 * Smaller dots when zoomed out, larger when zoomed in
 */
function calculatePointRadius(zoom: number, featureCount: number): number {
  // Reduce point size for large datasets to minimize overdraw and fragment shader invocations
  const isLarge = featureCount > 50000;
  if (zoom < 5) return isLarge ? 1 : 1.8; // Very zoomed out - smallest dots
  if (zoom < 10) return isLarge ? 1.8 : 2; // Zoomed out - small dots
  if (zoom < 15) return isLarge ? 2 : 2.5; // Medium zoom - medium dots
  return isLarge ? 2.5 : 3; // Zoomed in - larger dots
}

/**
 * Create deck.gl layers for GeoJSON data
 *
 * This function is used for ALL dataset points on the map, including:
 * - Workspace dataset points (from processed data with lat/lon columns)
 * - GeoJSON file layers
 * - Any other GeoJSON data added via addGeoJSONLayer
 *
 * Performance optimizations are automatically applied based on point count:
 * - >50,000 points: Data sampling to reduce rendering load
 * - >5,000 points: Performance mode (lower opacity, smaller radius, optimized rendering)
 * - <5,000 points: Full feature set with optimal visual quality
 */
// Create SVG data URIs for different shapes
function createShapeSVG(shape: GroupShape, color: string): string {
  const svg = (() => {
    // Use transparent background and optimize rendering to prevent border/shadow artifacts
    const svgAttrs =
      'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="background:transparent"';
    switch (shape) {
      case "circle":
        return `<svg ${svgAttrs}><circle cx="12" cy="12" r="10" fill="${color}"/></svg>`;
      case "square":
        // Scale to match circle size: circle diameter is 20, so square should be ~18x18 to account for visual perception
        // Centered: (24-18)/2 = 3, so x=3, y=3, width=18, height=18
        return `<svg ${svgAttrs}><rect x="3" y="3" width="18" height="18" rx="2" fill="${color}"/></svg>`;
      case "triangle":
        // Scale to match circle: triangle should fit within ~18x18 to match visual size
        // Base: from x=3 to x=21 (18 wide), height: from y=5 to y=20 (15 tall)
        return `<svg ${svgAttrs}><path d="M12 5 L21 20 L3 20 Z" fill="${color}"/></svg>`;
      case "star":
        // Scale star to match circle size - reduce by ~20% to account for angular shape appearing larger
        // Scale from original 20 units to ~16 units centered (12 ± 8)
        return `<svg ${svgAttrs}><path d="M12 4 L13.88 8.5 L18.8 9.2 L15.4 12.4 L16.3 17.2 L12 14.8 L7.7 17.2 L8.6 12.4 L5.2 9.2 L10.12 8.5 Z" fill="${color}"/></svg>`;
      case "diamond":
        // Scale to match circle: diamond should fit within ~18x18 to match visual size
        // Scale from 20 units to 18 units (12 ± 9)
        return `<svg ${svgAttrs}><path d="M12 4 L20 12 L12 20 L4 12 Z" fill="${color}"/></svg>`;
      default:
        return `<svg ${svgAttrs}><circle cx="12" cy="12" r="10" fill="${color}"/></svg>`;
    }
  })();
  // URL encode instead of base64 to avoid any encoding artifacts that might cause border appearance
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function createGeoJSONLayers(
  layerId: string,
  data: any,
  isVisible: boolean,
  groupConfigs?: Record<
    string,
    { name?: string; color?: string; shape?: GroupShape }
  >,
  visibleGroupsKey?: string,
  groupColumnName?: string | null,
  visibleGroups?: string[],
  zoom?: number,
  _pointConfigs?: Record<
    string,
    { color?: string; shape?: GroupShape; isVisible?: boolean }
  >,
  chunkConfigs?: Record<
    string,
    {
      displayName?: string;
      color?: string;
      shape?: GroupShape;
      from?: string;
      to?: string;
    }
  >,
  chunks?: Array<{
    value: string;
    label: string;
    values: string[];
    color: string;
    displayName?: string;
  }>,
  layerConfig?: { color?: string; shape?: GroupShape },
  visualizationMode?: "points" | "layers",
  visualizationLayerType?: "municipalities" | "countries" | "timezones" | null,
  staticData?: Record<string, any>,
  layerRegionConfigs?: Record<
    string,
    { displayName?: string; color?: string; isVisible?: boolean }
  >,
  cachedCategoryColorMap?: Record<string, string>, // Cached color map from store (initialized once)
  dataLabelsVisible: boolean = true // Data labels visibility toggle
): Layer[] {
  if (!data || !isVisible) {
    return [];
  }

  const layers: Layer[] = [];
  const geometryTypes = new Set<string>();
  let featureCount = 0;

  if (data?.features) {
    featureCount = data.features.length;
    data.features.forEach((feature: any) => {
      if (feature.geometry?.type) {
        geometryTypes.add(feature.geometry.type);
      }
    });
  }

  // Check if features have _category property for category-based coloring
  // We'll use groupConfigs for colors, but _category is needed to match groups
  const hasColorProperty = data?.features?.some(
    (f: any) => f.properties?._category || f.properties?._color
  );

  // Use cached category color map from store if available (initialized once on column selection)
  // Otherwise, build it from data (fallback for backward compatibility)
  // This ensures colors remain stable and are only assigned once on initial load
  let categoryColorMap = new Map<string, string>();
  
  if (cachedCategoryColorMap) {
    // Use cached color map from store - this was initialized once when column was selected
    // and remains stable even when layers are toggled
    Object.entries(cachedCategoryColorMap).forEach(([category, color]) => {
      // Override with groupConfigs color if user has configured it (allows runtime color changes)
      const configColor = groupConfigs?.[category]?.color;
      const finalColor =
        configColor && configColor.trim() !== "" ? configColor : color;
      categoryColorMap.set(category, finalColor);
    });
  } else if (data?.features) {
    // Fallback: Build color map from data (for backward compatibility or if cache is missing)
    const categories = new Set<string>();
    data.features.forEach((feature: any) => {
      const category = feature.properties?._category;
      if (category) {
        categories.add(String(category).trim());
      }
    });

    // Sort categories to ensure consistent order (same as sidebar)
    const sortedCategories = Array.from(categories).sort();
    
    // Assign default colors to categories based on sorted order
    sortedCategories.forEach((category, index) => {
      const configColor = groupConfigs?.[category]?.color;
      const defaultColor = groupColors[index % groupColors.length];
      const finalColor =
        configColor && configColor.trim() !== "" ? configColor : defaultColor;
      categoryColorMap.set(category, finalColor);
    });
  }

  // Don't filter data - create layers for all points and control visibility at layer level
  // This ensures all points are available and layers update properly when groups are toggled
  const filteredData = data;

  // Create a stable key for updateTriggers
  // Use visibleGroupsKey if provided to ensure layers update when groups change
  const filterKey = visibleGroupsKey || "unfiltered";

  // Normalize visibleGroups for comparison (trim whitespace, convert to string)
  const normalizedVisibleGroups = visibleGroups
    ? visibleGroups.map((g) => String(g).trim())
    : [];


  // Determine if we should use group-based layers (only if group column is selected)
  const useGroupLayers = !!groupColumnName;

  // Create a stable string representation of groupConfigs for updateTriggers
  // Objects are compared by reference, so we need a stable string representation
  const groupConfigsKey = groupConfigs
    ? Object.keys(groupConfigs)
        .sort()
        .map(
          (k) =>
            `${k}:${groupConfigs[k]?.color || ""}:${
              groupConfigs[k]?.shape || ""
            }`
        )
        .join(",")
    : "";

  // Create a stable string representation of chunkConfigs for updateTriggers
  // This ensures layers update when chunkConfigs change (e.g., when colors are updated in sidebar)
  const chunkConfigsKey = chunkConfigs
    ? Object.keys(chunkConfigs)
        .sort()
        .map(
          (k) =>
            `${k}:${chunkConfigs[k]?.color || ""}:${
              chunkConfigs[k]?.shape || ""
            }`
        )
        .join(",")
    : "";

  // Create a stable key for categoryColorMap to ensure colors update when column changes
  const categoryColorMapKey = cachedCategoryColorMap
    ? Object.keys(cachedCategoryColorMap)
        .sort()
        .map((k) => `${k}:${cachedCategoryColorMap[k] || ""}`)
        .join("|")
    : "";

  // Handle layer-based visualization mode
  if (
    visualizationMode === "layers" &&
    visualizationLayerType &&
    staticData?.[visualizationLayerType]
  ) {
    // categoryColorMap is already a Map, pass it directly
    return createLayerBasedVisualization(
      layerId,
      data,
      isVisible,
      visualizationLayerType,
      staticData[visualizationLayerType],
      groupColumnName ?? null,
      visibleGroups,
      groupConfigs,
      categoryColorMap,
      layerRegionConfigs,
      chunkConfigs,
      chunks,
      layerConfig,
      dataLabelsVisible
    );
  }

  // Handle Points (only if not in layer-based visualization mode)
  const isLayerMode = visualizationMode === "layers";

  if (geometryTypes.has("Point") && !isLayerMode) {
    const pointFeatures = filteredData?.features?.filter(
      (f: any) => f.geometry?.type === "Point"
    );


    if (pointFeatures && pointFeatures.length > 0) {
      const pointCount = pointFeatures.length;
      const currentZoom = zoom ?? 10; // Default to zoom 10 if not provided

      // Always show all points - no sampling
      // Optimize data structure: reuse feature objects directly to avoid unnecessary object creation
      // Only create minimal wrapper if needed for deck.gl compatibility
      const pointData: any[] = pointFeatures;


      // For large datasets, optimize ScatterplotLayer with performance-focused settings
      // deck.gl automatically handles viewport culling, so we optimize layer properties
      const isLargeDataset = pointCount > 5000;
      const isVeryLargeDataset = pointCount > 50000;

      // Picking enabled at all zoom levels for tooltips

      // Helper function to convert hex color to RGBA array
      const hexToRgba = (hex: string): [number, number, number, number] => {
        if (typeof hex === "string" && hex.startsWith("#")) {
          const hexClean = hex.replace("#", "");
          const r = parseInt(hexClean.substring(0, 2), 16);
          const g = parseInt(hexClean.substring(2, 4), 16);
          const b = parseInt(hexClean.substring(4, 6), 16);
          return [r, g, b, 255];
        }
        return [0, 51, 153, 255]; // Default color
      };

      // Pre-compute group info and colors for all points ONCE
      // Store directly on point objects to avoid Map lookups during rendering
      interface PointGroupInfo {
        category: string | null;
        shape: GroupShape;
        color: string;
        colorRgba: [number, number, number, number];
      }

      // Determine if we should use uncolored/ungrouped appearance
      // When groupColumnName is set but no groups are visible, show all points uncolored
      const shouldUseUncoloredAppearance =
        useGroupLayers && normalizedVisibleGroups.length === 0;

      // Helper function to generate point ID from coordinates and category
      const generatePointId = (point: any, category: string | null): string => {
        const coords = point.geometry?.coordinates;
        if (coords && Array.isArray(coords) && coords.length >= 2) {
          const lat = coords[1].toFixed(6);
          const lon = coords[0].toFixed(6);
          return category ? `${lat}-${lon}-${category}` : `${lat}-${lon}`;
        }
        // Fallback: use category or generate from properties
        return category || `point-${Math.random().toString(36).substr(2, 9)}`;
      };

      // Build a map from category value to chunk value for quick lookup
      // Filter out cluster-based chunks (starting with "cluster-") as they're for layer visualization
      // Only use regular chunks for point coloring
      const categoryToChunkMap = new Map<string, string>();
      const regularChunks =
        chunks?.filter((chunk) => !chunk.value.startsWith("cluster-")) || [];
      const useChunks = regularChunks.length > 0;


      if (useChunks) {
        regularChunks.forEach((chunk) => {
          chunk.values.forEach((value) => {
            categoryToChunkMap.set(value, chunk.value);
          });
        });
      }

      // Filter points based on visibility before processing
      // Simple unified logic that works for both chunks and non-chunked groups:
      // - When chunks are used: show points whose category belongs to a visible chunk
      // - When chunks are NOT used: show points whose category is in visibleGroups
      // - If data hasn't been regenerated yet (no _category), show all points
      // - If visibleGroups is empty, show all points (initial state)
      
      // Check if data has been regenerated with _category properties
      const hasCategoryData = pointData.some((point: any) => {
        const props = point.properties || point;
        return props?._category != null;
      });

      // Check if categories in data match the visible groups
      // If they don't match, the data hasn't been regenerated with the new column yet
      let categoriesMatchVisibleGroups = false;
      if (groupColumnName && hasCategoryData && normalizedVisibleGroups.length > 0) {
        const sampleCategories = new Set<string>();
        pointData.slice(0, 100).forEach((point: any) => {
          const category = (point.properties || point)?._category;
          if (category != null) {
            sampleCategories.add(String(category).trim());
          }
        });
        
        // Check if any sample categories match visible groups
        // If at least one matches, the data is likely correct
        categoriesMatchVisibleGroups = Array.from(sampleCategories).some((cat) =>
          normalizedVisibleGroups.includes(cat)
        );
      }


      const filteredPointData = pointData.filter((point: any) => {
        if (!groupColumnName) {
          // If no group column, show all points
          return true;
        }

        // If data hasn't been regenerated yet (no points have _category), show all points
        // This handles the case when column is first selected but data regeneration is pending
        if (!hasCategoryData) {
          return true;
        }

        const props = point.properties || point;
        
        // If data has categories but they don't match visible groups, use raw column value
        // This handles the case when column changes but data hasn't been regenerated yet
        let category: string | null = null;
        if (hasCategoryData && !categoriesMatchVisibleGroups && normalizedVisibleGroups.length > 0) {
          // Data hasn't been regenerated - use raw column value from properties
          const rawValue = props[groupColumnName];
          category = rawValue != null ? String(rawValue).trim() : null;
        } else {
          // Use _category from regenerated data
          category = props?._category;
        }
        
        // If this point doesn't have category, don't show it
        if (!category) {
          return false;
        }

        const normalizedCategory = String(category).trim();

        // If no visible groups specified (initial state), show all points
        if (normalizedVisibleGroups.length === 0) {
          return true;
        }

        if (useChunks) {
          // When using chunks: check if category belongs to a visible chunk
          if (categoryToChunkMap.has(normalizedCategory)) {
            const chunkValue = categoryToChunkMap.get(normalizedCategory)!;
            // Chunk is visible if all its values are in visibleGroups
            const chunk = regularChunks.find((c) => c.value === chunkValue);
            if (chunk) {
              return chunk.values.every((value) =>
                normalizedVisibleGroups.includes(String(value).trim())
              );
            }
          }
          // Category not in any chunk - don't show
          return false;
        } else {
          // When NOT using chunks: check if category is in visibleGroups
          // This is the simple case - just check if the category matches
          return normalizedVisibleGroups.includes(normalizedCategory);
        }
      });

      // Pre-compute all point info in a single pass
      // Attach directly to point objects for fastest accessor performance
      filteredPointData.forEach((point: any) => {
        const props = point.properties || point;
        // Explicitly ignore _category when groupColumnName is null
        // This ensures all points use the same color regardless of any _category in the data
        // If categories don't match visible groups, use raw column value (data hasn't been regenerated)
        let category: string | null = null;
        if (groupColumnName) {
          if (hasCategoryData && !categoriesMatchVisibleGroups) {
            // Data hasn't been regenerated - use raw column value from properties
            const rawValue = props[groupColumnName];
            category = rawValue != null ? String(rawValue).trim() : null;
          } else {
            // Use _category from regenerated data
            category = props?._category || null;
          }
        }
        let color = "#003399";
        let shape: GroupShape = "circle";

        // Generate point ID for point-level overrides (still needed for other purposes)
        const pointId = generatePointId(point, category);

        // If no group column is selected, use layer config if available, otherwise default blue
        // Ignore any _category that might exist in the data - all points should use the same color
        if (!groupColumnName) {
          if (layerConfig) {
            color = layerConfig.color || "#003399";
            shape = layerConfig.shape || "circle";
          } else {
            color = "#003399";
            shape = "circle";
          }
          // Explicitly ignore category when groupColumnName is null
          // This ensures all points use the same color regardless of any _category in the data
        }
        // When chunks are used, ONLY use cluster colors - no individual point colors
        else if (useChunks) {
          // Check if point is in a chunk (via category)
          if (category && categoryToChunkMap.has(category)) {
            const chunkValue = categoryToChunkMap.get(category)!;
            const chunkConfig = chunkConfigs?.[chunkValue];

            // chunkConfigs should always have colors from unified groups (same as sidebar)
            // If it doesn't exist or has empty color, something is wrong - use default
            const configColor = chunkConfig?.color?.trim();
            if (configColor && configColor !== "" && chunkConfig) {
              color = configColor;
              shape = chunkConfig.shape || "circle";
            } else {
              // This shouldn't happen if unified groups are set up correctly
              // But provide a fallback just in case
              color = "#003399";
              shape = "circle";
            }
          } else {
            // No category or category not in chunks - use default blue
            color = "#003399";
            shape = "circle";
          }
        } else {
          // When NOT using chunks, use the original logic for individual groups
          // If groups are disabled (no visible groups), use default uncolored appearance
          if (shouldUseUncoloredAppearance) {
            color = "#003399";
            shape = "circle";
          } else if (category) {
            // Normalize category value (trim whitespace) to ensure consistent lookup
            const normalizedCategory = String(category).trim();
            
            // Check groupConfigs first (user-configured colors and shapes from sidebar)
            // groupConfigs is built from unified groups and should always have colors
            // This ensures the map uses the same colors as shown in the sidebar
            if (groupConfigs?.[normalizedCategory]) {
              const configColor = groupConfigs[normalizedCategory].color?.trim();
              // groupConfigs should always have a color (from unified groups)
              // If it's empty, something is wrong - use categoryColorMap as fallback
              if (configColor && configColor !== "") {
                color = configColor;
                shape = groupConfigs[normalizedCategory].shape || "circle";
              } else {
                // Fallback to categoryColorMap if config color is missing (shouldn't happen)
                color = categoryColorMap.get(normalizedCategory) || "#003399";
                shape = "circle";
              }
            }
            // If groupConfigs doesn't have this category, check categoryColorMap
            else if (categoryColorMap.has(normalizedCategory)) {
              color = categoryColorMap.get(normalizedCategory)!;
              shape = "circle";
            }
            // Finally fall back to stored _color
            else if (props?._color) {
              color = props._color;
              shape = "circle";
            }
          } else if (props?._color) {
            color = props._color;
            shape = "circle";
          }
        }

        // Store point ID for later reference
        if (!point.properties) {
          point.properties = {};
        }
        point.properties._pointId = pointId;

        // Store pre-computed values directly on point for fastest access
        point._groupInfo = {
          category,
          shape,
          color,
          colorRgba: hexToRgba(color),
        } as PointGroupInfo;

        // Also store shape and color in properties so they're available when picked for tooltips
        // This ensures tooltips can access the correct color and shape even if categoryConfigs is not available
        if (!point.properties) {
          point.properties = {};
        }
        // Always store shape in properties so tooltip can access it
        // The tooltip reads from categoryConfigs first, but falls back to this
        point.properties._shape = shape;
        // Always store the computed color in properties (tooltip will use categoryConfigs first, but this is backup)
        point.properties._computedColor = color;
      });

      // Separate points by shape for optimal rendering
      const circlePoints: any[] = [];
      const iconPoints: any[] = [];
      const iconShapeMap = new Map<
        string,
        {
          shape: GroupShape;
          color: string;
          colorRgba: [number, number, number, number];
        }
      >();

      filteredPointData.forEach((point: any) => {
        const info = point._groupInfo as PointGroupInfo;
        if (!info) return;

        if (info.shape === "circle") {
          circlePoints.push(point);
        } else {
          iconPoints.push(point);
          // Store shape info for icon layer
          const iconKey = `${info.shape}-${info.color}`;
          if (!iconShapeMap.has(iconKey)) {
            iconShapeMap.set(iconKey, {
              shape: info.shape,
              color: info.color,
              colorRgba: info.colorRgba,
            });
          }
        }
      });

      // Filter points based on visibleGroups and chunks
      // Build a set of all valid category values from visible chunks/groups
      // Points must belong to a visible cluster/group to be shown
      const validCategoriesSet = new Set<string>();

      if (useGroupLayers) {
        // When chunks are used, visibleGroups contains category values (not chunk values)
        // because toggling a chunk adds/removes its category values to/from visibleGroups
        // So we can use visibleGroups directly as category values
        if (useChunks) {
          // visibleGroups already contains the category values from visible chunks
          // Just use them directly
          normalizedVisibleGroups.forEach((category) => {
            validCategoriesSet.add(category);
          });
        } else {
          // If not using chunks, use visibleGroups directly (they are category values)
          // If visibleGroups is empty, show all points (default behavior when column is first selected)
          if (normalizedVisibleGroups.length === 0) {
            // Show all points when no groups are explicitly visible (initial state)
            // This handles the case where column is changed but visibleGroups hasn't been set yet
            // Don't add any categories to validCategoriesSet - this will allow all points through
          } else {
            normalizedVisibleGroups.forEach((group) => {
              validCategoriesSet.add(group);
            });
          }
        }
      }

      const filterPoints = (points: any[]): any[] => {
        if (!useGroupLayers) {
          // No group column selected - return all points
          return points;
        }

        // Check if any points have categories in their properties (data has been regenerated with _category)
        const hasAnyCategories = points.some(
          (point) => point.properties?._category
        );

        // If data hasn't been regenerated yet (no points have _category in properties), show all points
        if (!hasAnyCategories) {
          return points;
        }

        // If visibleGroups is empty, show all points (data is being regenerated or initial state)
        if (normalizedVisibleGroups.length === 0) {
          return points;
        }

        // If no valid categories in the set, show all points (data mismatch - being regenerated)
        if (validCategoriesSet.size === 0) {
          return points;
        }

        // Check if any points match the visible groups - if none do, data is being regenerated
        const hasMatchingPoints = points.some((point) => {
          const info = point._groupInfo as PointGroupInfo | undefined;
          if (!info || !info.category) return false;
          return validCategoriesSet.has(info.category);
        });

        // If no points match the visible groups, show all points (data is being regenerated)
        if (!hasMatchingPoints) {
          return points;
        }

        // Filter points using Set lookup for O(1) performance
        return points.filter((point) => {
          const info = point._groupInfo as PointGroupInfo | undefined;
          if (!info) return false; // Hide points without group info

          const category = info.category;

          // Hide points without category when group column is selected
          if (!category) return false;

          // Only show points that belong to a visible cluster/group
          return validCategoriesSet.has(category);
        });
      };

      // Filter circle and icon points
      const filteredCirclePoints = filterPoints(circlePoints);
      // Note: filteredIconPoints would be used if icon layers are implemented
      // const filteredIconPoints = filterPoints(iconPoints);

      // Create single ScatterplotLayer for all circle points (filtered data)
      // Always create layer if there are any circle points
      if (circlePoints.length > 0) {
        // Include groupColumnName in layer ID to force recreation when column changes
        // This ensures points are properly recalculated with new _category values
        // Use "ungrouped" when groupColumnName is null to force layer recreation
        const stableLayerId = `${layerId}-points-circles${
          groupColumnName ? `-${groupColumnName}` : "-ungrouped"
        }`;

        // Pre-compute accessor functions outside of layer creation for better performance
        // Optimize accessors to be as simple as possible - deck.gl will inline these
        // Use direct property access for maximum performance
        const getPositionAccessor = (d: any) => {
          // Direct access - fastest path
          return d.geometry?.coordinates || d.position || [0, 0];
        };

        // Create color accessor that recalculates when categoryColorMap changes
        // This ensures colors update immediately when groupColumnName changes
        // The accessor dynamically looks up colors from categoryColorMap and groupConfigs
        // This is necessary because when groupColumnName changes, new layers are created
        // but the data might still have old _category values until it's regenerated
        const getFillColorAccessor = (d: any) => {
          const info = d._groupInfo;
          if (!info) return [0, 51, 153, 255];
          
          // If no group column is selected, use layer config color or default
          if (!groupColumnName) {
            const colorHex = layerConfig?.color || "#003399";
            return hexToRgba(colorHex);
          }
          
          // When chunks are used, check chunkConfigs instead of groupConfigs
          // The category maps to a chunk value, and we need to use the chunk's color
          if (useChunks && info.category) {
            const normalizedCategory = String(info.category).trim();
            if (categoryToChunkMap.has(normalizedCategory)) {
              const chunkValue = categoryToChunkMap.get(normalizedCategory)!;
              const chunkConfig = chunkConfigs?.[chunkValue];
              
              // chunkConfigs should have colors from unified groups (same as sidebar)
              const configColor = chunkConfig?.color?.trim();
              if (configColor && configColor !== "") {
                return hexToRgba(configColor);
              }
            }
            // If chunk lookup fails, fall back to pre-computed color
            return info.colorRgba || [0, 51, 153, 255];
          }
          
          // When NOT using chunks, check groupConfigs for individual group colors
          if (info.category) {
            const normalizedCategory = String(info.category).trim();
            
            // Check groupConfigs first (user-configured colors from unified groups)
            // This should match what's in the sidebar - groupConfigs is built from unified groups
            if (groupConfigs?.[normalizedCategory]) {
              const configColor = groupConfigs[normalizedCategory].color?.trim();
              if (configColor && configColor !== "") {
                return hexToRgba(configColor);
              }
            }
            
            // Fallback to categoryColorMap if groupConfigs doesn't have this category
            if (categoryColorMap?.has(normalizedCategory)) {
              return hexToRgba(categoryColorMap.get(normalizedCategory)!);
            }
          }
          
          // Fall back to pre-computed color (should already be correct)
          return info.colorRgba || [0, 51, 153, 255];
        };

        // Use constant 1.8px radius for all datasets and zoom levels
        const getRadiusAccessor = ((_d: any) => {
          // Constant 1.8px size for all datasets and zoom levels
          return 1.8;
        }) as any;

        // Prepare data for layer - use filtered or all points
        const layerData = useGroupLayers ? filteredCirclePoints : circlePoints;

        layers.push(
          new ScatterplotLayer({
            id: stableLayerId,
            data: layerData,
            pickable: true,
            visible: isVisible,
            // Reduce opacity when zoomed out for very large datasets to reduce overdraw
            // Lower opacity = less fragment shader work when many points overlap
            opacity: isVeryLargeDataset && currentZoom < 8 ? 0.7 : 1.0,
            // Disable stroked borders - simplified appearance, better performance
            stroked: false,
            filled: true,
            radiusScale: 1,
            // Use constant 1.8px size for all datasets
            radiusMinPixels: 1.8,
            radiusMaxPixels: 1.8,
            // Optimize sublayer props for large datasets - prevent unnecessary recalculations
            _subLayerProps: isLargeDataset
              ? {
                  points: {
                    updateTriggers: {
                      instancePositions: { type: "static" },
                      instanceColors: { type: "static" },
                      instanceRadius: { type: "static" },
                    },
                  },
                }
              : undefined,
            radiusUnits: "pixels",
            // No borders - simplified appearance
            lineWidthMinPixels: 0,
            lineWidthMaxPixels: 0,
            // Critical performance optimizations per deck.gl docs
            fp64: false, // Disable high precision - significant performance gain
            billboard: true, // Points always face camera - better performance
            coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
            autoHighlight: false, // Disable auto-highlight for performance
            // Optimize picking radius for large datasets - smaller radius = faster picking
            // Use extremely small picking radius for 100k+ points to reduce hit testing cost
            // 1px radius means only exact clicks will trigger picking, significantly reducing overhead
            getPickingRadius: isVeryLargeDataset
              ? 1
              : pointCount > 50000
              ? 2
              : 5,
            // Use pre-computed accessors
            getPosition: getPositionAccessor,
            getFillColor: getFillColorAccessor,
            // No borders - line color not used
            getLineColor: [255, 255, 255, 0],
            getRadius: getRadiusAccessor,
            // Optimize update triggers - radius is constant for all datasets
            updateTriggers: {
              getFillColor: [
                groupConfigsKey,
                chunkConfigsKey, // Update when chunkConfigs change (e.g., color updates)
                filterKey,
                shouldUseUncoloredAppearance,
                groupColumnName, // Force update when column changes
                layerConfig?.color || "", // Update when layerConfig color changes (for ungrouped points)
                categoryColorMapKey, // Force update when categoryColorMap changes (when column changes)
              ],
              getRadius: { type: "static" }, // Static since radius is always 1.8
              data: [
                filterKey,
                layerData.length,
                groupColumnName, // Force data recalculation when column changes
              ],
            },
          })
        );
      }

      // Create IconLayer for non-circle shapes (grouped by shape/color combination)
      // Always create layers if there are any icon points, even if all are filtered out
      if (iconPoints.length > 0) {
        // Build consolidated icon atlas with all shape/color combinations
        // This is more efficient than creating separate atlases per layer
        const consolidatedIconAtlas = new Map<string, string>();
        const consolidatedIconMapping: Record<
          string,
          {
            x: number;
            y: number;
            width: number;
            height: number;
            anchorY: number;
          }
        > = {};

        iconShapeMap.forEach(({ shape, color }, iconKey) => {
          const iconUrl = createShapeSVG(shape, color);
          consolidatedIconAtlas.set(iconKey, iconUrl);
          consolidatedIconMapping[iconUrl] = {
            x: 0,
            y: 0,
            width: 24,
            height: 24,
            anchorY: 12,
          };
        });

        iconShapeMap.forEach(({ shape, color }, iconKey) => {
          // Filter points for this shape/color combination using pre-computed info
          const shapeColorPoints = iconPoints.filter((point: any) => {
            const info = point._groupInfo as PointGroupInfo | undefined;
            return info && info.shape === shape && info.color === color;
          });

          // Apply group filtering to shape/color points
          const filteredShapeColorPoints = filterPoints(shapeColorPoints);

          if (filteredShapeColorPoints.length === 0) return;

          const iconUrl = consolidatedIconAtlas.get(iconKey)!;

          // Include groupColumnName in layer ID to force recreation when column changes
          const stableIconLayerId = `${layerId}-points-icons-${iconKey.replace(
            /[^a-zA-Z0-9]/g,
            "-"
          )}${groupColumnName ? `-${groupColumnName}` : ""}`;

          // Pre-compute accessors - optimized for performance
          const getIconPositionAccessor = (d: any) => {
            // Direct access - fastest path
            return d.geometry?.coordinates || d.position || [0, 0];
          };

          const getIconSizeAccessor = ((
            _d: any,
            { zoom }: { zoom: number }
          ) => {
            // More aggressive size reduction for very large datasets
            const baseSize = isVeryLargeDataset ? 6 : isLargeDataset ? 8 : 12;
            if (zoom < 4) return baseSize * 0.4; // Very small when extremely zoomed out
            if (zoom < 6) return baseSize * 0.5; // Small when very zoomed out
            if (zoom < 10) return baseSize * 0.75; // Medium-small when zoomed out
            if (zoom < 15) return baseSize; // Normal size
            return baseSize * 1.25; // Larger when zoomed in
          }) as any;

          const iconLayerData = useGroupLayers
            ? filteredShapeColorPoints
            : shapeColorPoints;

          layers.push(
            new IconLayer({
              id: stableIconLayerId,
              data: iconLayerData,
              pickable: true,
              visible: isVisible,
              iconAtlas: iconUrl,
              iconMapping: consolidatedIconMapping,
              getIcon: () => iconUrl, // Constant function - minimal overhead
              coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
              getPosition: getIconPositionAccessor,
              getSize: getIconSizeAccessor,
              sizeUnits: "pixels",
              // More aggressive size reduction for very large datasets
              sizeMinPixels: isVeryLargeDataset ? 2 : 3,
              sizeMaxPixels: isVeryLargeDataset ? 12 : 16,
              // Optimize picking radius for large datasets - use very small radius
              getPickingRadius: isVeryLargeDataset ? 1 : 5,
              updateTriggers: {
                getSize: [], // Size updates with zoom, keep dynamic
                getIcon: [chunkConfigsKey, groupConfigsKey], // Update when chunk/group configs change
                data: [filterKey, iconLayerData.length],
              },
            })
          );
        });
      }
    }
  }

  // Handle MultiPoint
  if (geometryTypes.has("MultiPoint")) {
    const multiPointFeatures = filteredData?.features?.filter(
      (f: any) => f.geometry?.type === "MultiPoint"
    );

    if (multiPointFeatures && multiPointFeatures.length > 0) {
      // Flatten MultiPoint to individual points
      const flattenedPoints = multiPointFeatures.flatMap((feature: any) => {
        return feature.geometry.coordinates.map((coord: number[]) => ({
          geometry: { type: "Point", coordinates: coord },
          properties: feature.properties,
        }));
      });

      layers.push(
        new ScatterplotLayer({
          id: `${layerId}-multipoint`,
          data: flattenedPoints,
          pickable: true,
          visible: isVisible,
          opacity: 1.0, // Fully opaque for solid color
          stroked: false, // No borders - simplified appearance
          filled: true,
          radiusScale: 1,
          radiusMinPixels: 1.8,
          radiusMaxPixels: 7,
          lineWidthMinPixels: 0, // No borders
          lineWidthMaxPixels: 0, // No borders
          getPosition: (d: any) => {
            const coords = d.geometry?.coordinates;
            return coords ? [coords[0], coords[1]] : [0, 0];
          },
          getFillColor: (d: any) => {
            const props = d.properties || d;
            const category = props?._category;

            // Prioritize groupConfigs color (from sidebar selection), then categoryColorMap, then stored _color
            let color = "#003399"; // Default fallback color

            if (category) {
              // Normalize category value (trim whitespace) to ensure consistent lookup
              const normalizedCategory = String(category).trim();
              
              // First check groupConfigs (user-configured colors from sidebar)
              if (groupConfigs?.[normalizedCategory]?.color) {
                color = groupConfigs[normalizedCategory].color;
              }
              // Then check categoryColorMap (default colors based on category order)
              else if (categoryColorMap.has(normalizedCategory)) {
                color = categoryColorMap.get(normalizedCategory)!;
              }
              // Finally fall back to stored _color
              else if (props?._color) {
                color = props._color;
              }
            } else if (props?._color) {
              // If no category, use stored _color
              color = props._color;
            }

            if (typeof color === "string" && color.startsWith("#")) {
              const hex = color.replace("#", "");
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              return [r, g, b, 255];
            }
            return [0, 51, 153, 255]; // Default #003399, fully opaque
          },
          getLineColor: [255, 255, 255, 0], // No borders
          getRadius: ((_d: any, { zoom }: { zoom: number }) => {
            return calculatePointRadius(zoom, featureCount);
          }) as any,
        })
      );
    }
  }

  // Handle LineString
  if (geometryTypes.has("LineString")) {
    const lineFeatures = filteredData?.features?.filter(
      (f: any) => f.geometry?.type === "LineString"
    );

    if (lineFeatures && lineFeatures.length > 0) {
      layers.push(
        new PathLayer({
          id: `${layerId}-line`,
          data: lineFeatures,
          pickable: true,
          visible: isVisible,
          widthScale: 1,
          widthMinPixels: 1,
          widthMaxPixels: 5,
          getPath: (d: any) => d.geometry?.coordinates || [],
          getColor: [0, 51, 153, 204], // Default #003399 with 0.8 opacity
          getWidth: ((_d: any, { zoom }: { zoom: number }) => {
            if (zoom < 0) return featureCount > 1000 ? 1.8 : 2.5;
            if (zoom < 10) return featureCount > 1000 ? 2.5 : 3;
            return 3;
          }) as any,
          updateTriggers: {
            getWidth: [featureCount],
          },
        })
      );
    }
  }

  // Handle MultiLineString
  if (geometryTypes.has("MultiLineString")) {
    const multiLineFeatures = filteredData?.features?.filter(
      (f: any) => f.geometry?.type === "MultiLineString"
    );

    if (multiLineFeatures && multiLineFeatures.length > 0) {
      // Flatten MultiLineString to individual paths
      const flattenedPaths = multiLineFeatures.flatMap((feature: any) => {
        return feature.geometry.coordinates.map((coords: number[][]) => ({
          geometry: { type: "LineString", coordinates: coords },
          properties: feature.properties,
        }));
      });

      layers.push(
        new PathLayer({
          id: `${layerId}-multiline`,
          data: flattenedPaths,
          pickable: true,
          visible: isVisible,
          widthScale: 1,
          widthMinPixels: 1,
          widthMaxPixels: 5,
          getPath: (d: any) => d.geometry?.coordinates || [],
          getColor: [0, 51, 153, 204], // Default #003399 with 0.8 opacity
          getWidth: 3,
        })
      );
    }
  }

  // Handle Polygon and MultiPolygon using GeoJsonLayer
  if (geometryTypes.has("Polygon") || geometryTypes.has("MultiPolygon")) {
    const polygonFeatures = filteredData?.features?.filter(
      (f: any) =>
        f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon"
    );

    if (polygonFeatures && polygonFeatures.length > 0) {
      // Fill layer
      layers.push(
        new GeoJsonLayer({
          id: `${layerId}-fill`,
          data: {
            type: "FeatureCollection",
            features: polygonFeatures,
          },
          pickable: true,
          visible: isVisible,
          stroked: false,
          filled: true,
          fp64: true, // Enable high precision for accurate polygon alignment with map tiles
          getFillColor: (d: any) => {
            let color = d.properties?._color || "#003399";

            // Override with group config color if available
            const category = d.properties?._category;
            if (category && groupConfigs?.[category]?.color) {
              color = groupConfigs[category].color;
            }

            if (hasColorProperty && color) {
              if (typeof color === "string" && color.startsWith("#")) {
                const hex = color.replace("#", "");
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const opacity = featureCount > 1000 ? 38 : 77; // 0.15 or 0.3
                return [r, g, b, opacity];
              }
            }
            const opacity = featureCount > 1000 ? 38 : 77; // 0.15 or 0.3
            return [0, 51, 153, opacity]; // Default #003399
          },
          getFillOpacity: ((_d: any, { zoom }: { zoom: number }) => {
            if (zoom < 0) return featureCount > 1000 ? 0.15 : 0.3;
            if (zoom < 10) return featureCount > 1000 ? 0.25 : 0.3;
            return 0.3;
          }) as any,
          updateTriggers: {
            getFillColor: [
              hasColorProperty,
              filterKey,
              featureCount,
              groupConfigsKey,
            ],
            getFillOpacity: [featureCount],
          },
        })
      );

      // Border layer
      // Border layers removed - simplified appearance
    }
  }

  return layers;
}

/**
 * Create layer-based visualization (municipalities, countries, timezones)
 * Aggregates point data within each region and displays colored regions with labels
 */
function createLayerBasedVisualization(
  layerId: string,
  pointData: any,
  isVisible: boolean,
  layerType: "municipalities" | "countries" | "timezones",
  layerGeoJSON: any,
  groupColumnName: string | null,
  _visibleGroups: string[] | undefined,
  groupConfigs?: Record<
    string,
    { name?: string; color?: string; shape?: GroupShape }
  >,
  categoryColorMap?: Map<string, string> | Record<string, string>,
  layerRegionConfigs?: Record<
    string,
    { displayName?: string; color?: string; isVisible?: boolean }
  >,
  chunkConfigs?: Record<
    string,
    {
      displayName?: string;
      color?: string;
      shape?: GroupShape;
      from?: string;
      to?: string;
    }
  >,
  _chunks?: Array<{
    value: string;
    label: string;
    values: string[];
    color: string;
    displayName?: string;
  }>,
  layerConfig?: { color?: string; shape?: GroupShape },
  dataLabelsVisible: boolean = true // Data labels visibility toggle
): Layer[] {
  // Allow rendering even if pointData is empty - regions will show with default colors
  if (!layerGeoJSON || !isVisible) {
    return [];
  }

  const layers: Layer[] = [];

  // Extract point features (allow empty - regions will show with default colors)
  const pointFeatures =
    pointData?.features?.filter((f: any) => f.geometry?.type === "Point") || [];

  if (!layerGeoJSON?.features || layerGeoJSON.features.length === 0) {
    return [];
  }

  // Pre-compute bounding boxes for all features to optimize point-in-polygon checks
  const featureBBoxes = layerGeoJSON.features.map(
    (feature: GeoJSON.Feature) => {
      const bbox = featureBoundingBox(feature);
      return bbox;
    }
  );

  // Process each layer feature (municipality, country, or timezone)
  const processedFeatures = layerGeoJSON.features
    .map((feature: GeoJSON.Feature, index: number) => {
      const regionId = `layer-${layerType}-${index}`;
      const bbox = featureBBoxes[index];

      // Find all points inside this feature
      // First filter by bounding box (fast), then do point-in-polygon check (slow)
      const pointsInside: any[] = [];

      if (bbox) {
        for (const pointFeature of pointFeatures) {
          const pointCoords = pointFeature.geometry.coordinates as [
            number,
            number
          ];

          // Quick bounding box check first
          if (pointInBoundingBox(pointCoords, bbox)) {
            // Only do expensive point-in-polygon check if point is in bounding box
            if (pointInGeoJSONFeature(pointCoords, feature)) {
              pointsInside.push(pointFeature);
            }
          }
        }
      }

      // Aggregate numeric values from points inside
      const aggregatedValues: Record<string, number> = {};
      const pointCount = pointsInside.length;

      // Calculate dominant category from points inside (for color matching with points)
      // This ensures polygon layers use the same colors as points
      let dominantCategory: string | null = null;
      if (pointsInside.length > 0 && groupColumnName) {
        const categoryCounts = new Map<string, number>();
        pointsInside.forEach((point) => {
          const category = point.properties?._category;
          if (category) {
            const normalizedCategory = String(category).trim();
            categoryCounts.set(
              normalizedCategory,
              (categoryCounts.get(normalizedCategory) || 0) + 1
            );
          }
        });
        // Find the most common category
        let maxCount = 0;
        categoryCounts.forEach((count, category) => {
          if (count > maxCount) {
            maxCount = count;
            dominantCategory = category;
          }
        });
        // If there's a tie, use the first one in sorted order (to match categoryColorMap ordering)
        if (maxCount > 0) {
          const categoriesWithMaxCount = Array.from(categoryCounts.entries())
            .filter(([, count]) => count === maxCount)
            .map(([category]) => category)
            .sort();
          dominantCategory = categoriesWithMaxCount[0];
        }
      }

      // Calculate cluster value: average of numeric values, or count non-numeric values
      let clusterValue: string | number = pointCount; // Default to point count

      if (pointsInside.length > 0) {
        // Get all property keys from points (excluding group column and internal properties)
        const allKeys = new Set<string>();
        pointsInside.forEach((point) => {
          Object.keys(point.properties || {}).forEach((key) => {
            if (
              (!groupColumnName || key !== groupColumnName) &&
              key !== "_category" &&
              key !== "_color"
            ) {
              allKeys.add(key);
            }
          });
        });

        // Calculate average of numeric values and determine cluster value
        let hasNumericValues = false;
        let totalNumericAverage = 0;
        let numericKeyCount = 0;

        allKeys.forEach((key) => {
          const values = pointsInside
            .map((point) => {
              const val = point.properties?.[key];
              if (val === null || val === undefined || val === "") {
                return null;
              }
              const num =
                typeof val === "number" ? val : parseFloat(String(val));
              return isNaN(num) ? null : num;
            })
            .filter((v) => v !== null) as number[];

          if (values.length > 0) {
            const sum = values.reduce((s, val) => s + val, 0);
            const average = sum / values.length;
            aggregatedValues[key] = average;
            totalNumericAverage += average;
            numericKeyCount++;
            hasNumericValues = true;
          }
        });

        // Use average of numeric values if available
        if (hasNumericValues && numericKeyCount > 0) {
          const avg = totalNumericAverage / numericKeyCount; // Average across all numeric keys
          // Round to 4 decimal places to ensure identical averages are grouped together
          clusterValue = Math.round(avg * 10000) / 10000;
        } else if (groupColumnName) {
          // For non-numeric values, count unique values (only if groupColumnName exists)
          const uniqueValues = new Set<string>();
          pointsInside.forEach((point) => {
            const val = point.properties?.[groupColumnName];
            if (val != null && val !== "") {
              uniqueValues.add(String(val).trim());
            }
          });
          clusterValue = uniqueValues.size > 0 ? uniqueValues.size : pointCount;
        }
        // Otherwise, clusterValue remains as pointCount
      }

      // Create new feature with aggregated data and cluster value
      return {
        ...feature,
        properties: {
          ...feature.properties,
          _pointCount: pointCount,
          _aggregatedValues: aggregatedValues,
          _hasData: pointCount > 0,
          _clusterValue: clusterValue, // Store cluster value for grouping
          _regionId: regionId,
          _dominantCategory: dominantCategory, // Store dominant category for color matching with points
        },
      };
    })
    // Filter by visibility and data presence
    // Only show regions that:
    // 1. Are explicitly configured to be visible (isVisible: true), OR
    // 2. Have data (pointCount > 0) and no explicit visibility config (default to visible)
    // Filter out regions that:
    // - Are explicitly set to invisible (isVisible: false), OR
    // - Have no data AND no explicit visibility config (undefined layers)
    // - For brick layers: belong to a count group that is not visible
    .filter((feature: any) => {
      const regionId = feature.properties?._regionId;
      if (!regionId) return true; // Keep features without regionId (shouldn't happen, but safe fallback)
      
      const config = layerRegionConfigs?.[regionId];
      const hasData = feature.properties?._hasData === true;
      const isVisible = config?.isVisible;
      
      // If explicitly set to invisible, filter out
      if (isVisible === false) {
        return false;
      }
      
      // If explicitly set to visible, keep it
      if (isVisible === true) {
        return true;
      }
      
      // For brick layers (when groupColumnName is null), check if the count group/chunk is visible
      if (!groupColumnName && hasData) {
        const pointCount = feature.properties?._pointCount || 0;
        const countString = String(pointCount);
        
        if (_visibleGroups && _visibleGroups.length > 0) {
          // First check if this count belongs to a visible chunk
          if (_chunks && chunkConfigs) {
            for (const chunk of _chunks) {
              // Get chunk range from chunkConfigs (from/to) or parse from label
              const chunkConfig = chunkConfigs[chunk.value];
              let from: number;
              let to: number;
              
              if (chunkConfig?.from && chunkConfig?.to) {
                from = parseInt(chunkConfig.from, 10);
                to = parseInt(chunkConfig.to, 10);
              } else {
                // Fallback: parse chunk range from label (e.g., "0-10" or "0")
                const labelParts = chunk.label.split("-");
                from = parseInt(labelParts[0], 10);
                to = labelParts.length > 1 ? parseInt(labelParts[1], 10) : from;
              }
              
              if (pointCount >= from && pointCount <= to) {
                // This count belongs to this chunk - check if chunk is visible
                return _visibleGroups.includes(chunk.value);
              }
            }
          }
          
          // If not in a chunk, check if the individual count group is visible
          return _visibleGroups.includes(countString);
        }
        // If no visibleGroups specified, show all (default behavior)
        return true;
      }
      
      // If no explicit config, only show if it has data (default behavior for regions with data)
      return hasData;
    });

  // Group features by cluster value and assign colors
  // Identical cluster values get the same color
  const clusterValueMap = new Map<string | number, number>(); // clusterValue -> color index
  const clusterColors: [number, number, number, number][] = [];

  // First pass: build cluster map for all features with cluster values
  processedFeatures.forEach((feature: any) => {
    const clusterValue = feature.properties?._clusterValue;
    if (clusterValue === undefined) return;

    if (!clusterValueMap.has(clusterValue)) {
      const colorIndex = clusterValueMap.size;
      const colorHex = groupColors[colorIndex % groupColors.length];
      const hex = colorHex.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const fillColor: [number, number, number, number] = [r, g, b, 255];
      clusterValueMap.set(clusterValue, colorIndex);
      clusterColors.push(fillColor);
    }
  });

  // Helper function to extract index from regionId (format: "layer-{type}-{index}")
  const getIndexFromRegionId = (regionId: string | undefined): number | null => {
    if (!regionId) return null;
    const match = regionId.match(/layer-\w+-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  };

  // Second pass: assign colors to all features
  // Color priority when groupColumnName is null: layerRegionConfigs.color > layerConfig.color > default blue
  // Color priority when groupColumnName is set: layerRegionConfigs.color > chunkConfigs.color > index-based default color (to match sidebar)
  // For brick layers (visualizationMode === "layers"), use layerRegionConfigs for individual regions
  const featuresWithColors = processedFeatures.map((feature: any) => {
    const clusterValue = feature.properties?._clusterValue;
    let fillColor: [number, number, number, number] = [200, 200, 200, 255]; // Light gray default for regions without data

    const regionId = feature.properties?._regionId;
    const regionConfig = layerRegionConfigs?.[regionId];
    
    // For brick layers (when groupColumnName is null), use layerRegionConfigs for individual regions
    if (!groupColumnName) {
      // Priority 1: Check layer region config for custom color (highest priority for brick layers)
      if (regionConfig?.color) {
        const colorHex = regionConfig.color;
        if (colorHex.startsWith("#")) {
          const hex = colorHex.replace("#", "");
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          fillColor = [r, g, b, 255];
        }
      } else {
        // Fallback to layer config color if no region config color found
        const colorHex = layerConfig?.color || "#003399";
        if (colorHex.startsWith("#")) {
          const hex = colorHex.replace("#", "");
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          fillColor = [r, g, b, 255];
        }
      }
    }
    // Priority 1: Check layer region config for custom color (highest priority)
    // This matches the sidebar logic: regionConfig?.color || groupColors[index % groupColors.length]
    else if (regionConfig?.color) {
      const colorHex = regionConfig.color;
      if (colorHex.startsWith("#")) {
        const hex = colorHex.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        fillColor = [r, g, b, 255];
      }
    } else if (feature.properties?._hasData) {
      // Priority 2: Check chunk configs for cluster color (only if no layerRegionConfig color and has data)
      let hasCustomColor = false;
      if (clusterValue !== undefined) {
        const clusterValueString = `cluster-${clusterValue}`;
        const clusterConfig = chunkConfigs?.[clusterValueString];

        if (clusterConfig?.color) {
          // Use cluster config color
          const colorHex = clusterConfig.color;
          if (colorHex.startsWith("#")) {
            const hex = colorHex.replace("#", "");
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            fillColor = [r, g, b, 255];
            hasCustomColor = true;
          }
        }
      }
      
      // Priority 3: Use category-based color from categoryColorMap to match point colors
      // This ensures polygon layers use the same colors as points for the same categories
      if (!hasCustomColor) {
        const dominantCategory = feature.properties?._dominantCategory;
        if (dominantCategory) {
          // Check groupConfigs first (user-configured colors), then categoryColorMap (default colors)
          // This matches the priority used for points in createGeoJSONLayers
          let colorHex: string | undefined;
          if (groupConfigs?.[dominantCategory]?.color) {
            const configColor = groupConfigs[dominantCategory].color;
            if (configColor && configColor.trim() !== "") {
              colorHex = configColor;
            }
          }
          if (!colorHex) {
            // Handle both Map and Record types
            if (categoryColorMap instanceof Map) {
              if (categoryColorMap.has(dominantCategory)) {
                colorHex = categoryColorMap.get(dominantCategory);
              }
            } else if (categoryColorMap && typeof categoryColorMap === 'object') {
              colorHex = categoryColorMap[dominantCategory];
            }
          }
          
          if (colorHex) {
            if (colorHex.startsWith("#")) {
              const hex = colorHex.replace("#", "");
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              fillColor = [r, g, b, 255];
            }
          } else {
            // Fallback to index-based default color if no dominant category or category not in map
            const featureIndex = getIndexFromRegionId(regionId);
            if (featureIndex !== null) {
              const colorHex = groupColors[featureIndex % groupColors.length];
              const hex = colorHex.replace("#", "");
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              fillColor = [r, g, b, 255];
            } else if (clusterValue !== undefined && clusterValueMap.has(clusterValue)) {
              // Fallback to cluster-based color if index extraction fails
              const colorIndex = clusterValueMap.get(clusterValue)!;
              fillColor = clusterColors[colorIndex];
            }
          }
        }
      }
    }

    return {
      ...feature,
      properties: {
        ...feature.properties,
        _fillColor: fillColor,
      },
    };
  });

  // Only create layer if there are features to display
  if (featuresWithColors.length === 0) {
    return layers;
  }

  // Filter features based on layerRegionConfigs visibility (for brick layers with individual regions)
  const visibleFeatures = featuresWithColors.filter((feature: any) => {
    const regionId = feature.properties?._regionId;
    
    // If no groupColumnName (brick layer mode), check layerRegionConfigs visibility
    if (!groupColumnName && layerRegionConfigs) {
      const regionConfig = layerRegionConfigs[regionId];
      // If region config exists, use its visibility (default to true if not specified)
      if (regionConfig !== undefined) {
        return regionConfig.isVisible !== false;
      }
      // If no config exists, show the region (default behavior)
      return true;
    }
    
    // For column-based mode, show all features (visibility handled by group visibility)
    return true;
  });

  // Create GeoJSON layer for regions
  const processedGeoJSON: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: visibleFeatures,
  };

  // Include groupColumnName in layer ID to force recreation when column changes
  // Use "ungrouped" when groupColumnName is null to force layer recreation
  const layerIdSuffix = groupColumnName ? `-${groupColumnName}` : "-ungrouped";
  layers.push(
    new GeoJsonLayer({
      id: `${layerId}-${layerType}-regions${layerIdSuffix}`,
      data: processedGeoJSON,
      pickable: true,
      stroked: true,
      filled: true,
      visible: isVisible,
      fp64: true, // Enable high precision for accurate polygon alignment with map tiles
      lineWidthMinPixels: 1,
      getFillColor: (feature: any) => {
        return feature.properties?._fillColor || [0, 51, 153, 255];
      },
      getLineColor: [200, 200, 200, 255],
      getLineWidth: 1,
      updateTriggers: {
        getFillColor: [
          clusterValueMap.size,
          groupColumnName,
          layerConfig?.color || "", // Update when layerConfig color changes (for ungrouped layers)
          // Track layerRegionConfigs changes (color and visibility) to trigger color updates
          layerRegionConfigs
            ? Object.keys(layerRegionConfigs)
                .sort()
                .map(
                  (k) =>
                    `${k}:${layerRegionConfigs[k]?.color || ""}:${
                      layerRegionConfigs[k]?.isVisible ? "1" : "0"
                    }`
                )
                .join(",")
            : "",
        ], // Update when cluster count, column, layerConfig, or layer region configs change
        data: [
          featuresWithColors.length,
          groupColumnName, // Force data recalculation when column changes
          clusterValueMap.size, // Force recalculation when cluster values change
          // Include layerRegionConfigs visibility changes to trigger data filtering
          layerRegionConfigs
            ? Object.keys(layerRegionConfigs)
                .sort()
                .map((k) => `${k}:${layerRegionConfigs[k]?.isVisible ? "1" : "0"}`)
                .join(",")
            : "",
        ],
      },
    })
  );

  // Create text labels for regions using TextLayer (sharp rendering with SDF)
  // Use visibleFeatures to match what's shown on the map
  const labelData = visibleFeatures
    .filter((feature: any) => feature.properties?._hasData)
    .map((feature: any) => {
      const centroid = featureCentroid(feature);
      if (!centroid) return null;

      // Get point count - only show the number
      const pointCount = feature.properties?._pointCount || 0;
      const labelText = pointCount > 0 ? String(pointCount) : "";

      if (!labelText) return null;

      return {
        position: centroid,
        text: labelText,
      };
    })
    .filter(
      (
        item: { position: [number, number]; text: string } | null
      ): item is { position: [number, number]; text: string } => item !== null
    );

  // Use official TextLayer from deck.gl for sharp text rendering
  if (labelData.length > 0) {
    layers.push(
      new TextLayer({
        id: `${layerId}-${layerType}-labels`,
        data: labelData,
        pickable: false,
        // Data accessors
        getPosition: (d: any) => d.position,
        getText: (d: any) => d.text,
        getColor: [0, 0, 0, 255], // Black text
        getSize: 14, // Keep same size - sharpness comes from SDF quality, not size
        getAngle: 0,
        getTextAnchor: "middle",
        getAlignmentBaseline: "center",
        // Font settings
        fontFamily: "Arial, sans-serif",
        fontWeight: "bold",
        // Size constraints
        sizeScale: 1,
        sizeUnits: "pixels",
        sizeMinPixels: 12,
        sizeMaxPixels: 16,
        // Outline properties for visibility - more prominent outline
        outlineColor: [255, 255, 255, 255], // White outline
        outlineWidth: 8, // Thick outline for maximum visibility
        // Rendering options
        billboard: true, // Text always faces camera
        // Font settings for maximum sharpness (SDF)
        // Higher fontSize in SDF creates sharper text at the same display size
        fontSettings: {
          sdf: true, // Use signed distance fields for sharper text
          fontSize: 64, // Higher base font size for SDF = sharper rendering at display size
          buffer: 24, // Increased buffer for better SDF quality (proportional to fontSize)
          radius: 16, // Increased radius for smoother, sharper edges
          cutoff: 0.25, // SDF cutoff for crisp edges
        },
        // Character set optimization (only numbers needed)
        characterSet: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
        visible: isVisible && dataLabelsVisible,
        coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
        // Update triggers to prevent unnecessary re-renders
        updateTriggers: {
          getText: [
            labelData.length,
            groupColumnName, // Update when column changes
            // Include layerRegionConfigs visibility changes to update labels when regions are shown/hidden
            layerRegionConfigs
              ? Object.keys(layerRegionConfigs)
                  .sort()
                  .map((k) => `${k}:${layerRegionConfigs[k]?.isVisible ? "1" : "0"}`)
                  .join(",")
              : "",
          ],
          getPosition: [
            labelData.length,
            groupColumnName, // Update when column changes
            // Include layerRegionConfigs visibility changes
            layerRegionConfigs
              ? Object.keys(layerRegionConfigs)
                  .sort()
                  .map((k) => `${k}:${layerRegionConfigs[k]?.isVisible ? "1" : "0"}`)
                  .join(",")
              : "",
          ],
        },
      })
    );
  }

  return layers;
}
